import { BookingEventType } from '@deltager/organizers/admin/modules/booking-event-setup/enums/booking-event-type.enum';
import { VenueType } from '@deltager/organizers/admin/modules/booking-event-setup/enums/venue-type.enum';
import { VenueLocation } from '@deltager/organizers/admin/modules/booking-event-setup/models/venue-location.model';
import { BookingEventSettings } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-event-settings.model';
import { BookingPriceCategory } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-price-category.model';
import { BookingSystemField } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-system-field.model';
import { BookingCustomField } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-custom-field.model';
import { BookingAvailabilityTimeSlot } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-availability-time-slot.model';
import { BookingAvailabilityDay } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-availability-day.model';
import { BookingEventConsent } from '@deltager/organizers/admin/modules/booking-event-setup/models/booking-event-consent.model';
import { ContactDetails } from '@deltager/organizers/admin/modules/booking-event-setup/models/contact-details.model';
import { DayOfWeek } from '@deltager/organizers/admin/shared/enums/day-of-week.enum';
import { Timespan } from '@deltager/organizers/admin/modules/booking-event-setup/models/timespan.model';
import { BookingLocationType } from '@deltager/organizers/admin/modules/booking-event-setup/enums/booking-location-type.enum';
import { FileData } from './FileData';
import { Image } from '@deltager/organizers/admin/modules/events/create-event/model/image.model';
import { BookingTags } from '../../modules/booking-event-setup/pages/main/components/booking-event-settings/booking-tags/booking-tags.component';
import { BookingEventSetupWizardStep } from '@deltager/organizers/admin/modules/booking-event-setup/pages/main/components/wizard/booking-event-setup-wizard-step.enum';
import { VatType } from '../../modules/events/create-event/model/vat-type.enum';

// This and all related models should be `interface` instead of `class` for better ngrx store support
export interface BookingEvent {
  id: number;
  name: string;
  type: BookingEventType;
  organizerId: number;
  availableFrom?: Date | string;
  availableTo?: Date | string;
  sessionDuration: string;
  selectedVenueTypes: VenueType[];
  locationType: BookingLocationType;
  location?: VenueLocation;
  contactDetails?: ContactDetails;
  companyNumber?: string;
  passportNumber?: string;
  isPaid: boolean;
  isDraft?: boolean;
  isPublished: boolean;
  bankAccountId?: number;
  payoutReference?: string;
  description: string;
  eventUrl?: string;
  registrationPageBaseUrl?: string;
  relativeRegistrationPageUrl?: string;
  vat: VatType;
  settings: BookingEventSettings;
  priceCategories: BookingPriceCategory[];
  systemFields: BookingSystemField[];
  customFields: BookingCustomField[];
  availabilityTimeSlots: BookingAvailabilityTimeSlot[];
  availabilityDays: BookingAvailabilityDay[];
  consents: BookingEventConsent[];
  bookingPageTemplateId: number;
  bookingsCount: number;
  isFullDayDuration: boolean;
  eventId?: number;
  payoutDelayDays?: number;
  bookingSetupStep: BookingEventSetupWizardStep;
  documentUrls?: string[];
  documents?: FileData[];
  images?: Image[];
  categories: BookingTags[];
  totalRegistered: number;

  createdById?: number;
  timezone: string;
}

export function buildBookingEvent(
  fullName: string,
  email: string,
  phoneNumber: string,
  companyNumber: string,
  passportNumber: string,
): BookingEvent {
  const bookingEvent = {} as BookingEvent;
  bookingEvent.contactDetails = { name: fullName, email: email, phoneNumber: phoneNumber } as ContactDetails;
  bookingEvent.companyNumber = companyNumber;
  bookingEvent.passportNumber = passportNumber;
  bookingEvent.isFullDayDuration = false;
  bookingEvent.vat = VatType.NO_VAT;
  bookingEvent.isPublished = false;
  bookingEvent.description = '';

  bookingEvent.availabilityTimeSlots = Object.keys(DayOfWeek)
    .filter(key => !isNaN(+DayOfWeek[key]))
    .map(key => +DayOfWeek[key])
    .sort((a, b) => (b === 0 ? -1 : a - b))
    .map(w => {
      return {
        id: 0,
        dayOfWeek: w,
        startTime: Timespan.getDefaultStartTime().format(),
        endTime: Timespan.getDefaultEndTime().format(),
      } as BookingAvailabilityTimeSlot;
    });

  bookingEvent.sessionDuration = new Timespan(0, 15).format();

  return bookingEvent;
}

export function mapBookingEvent(initialData: BookingEvent): BookingEvent {
  if (initialData) {
    const bookingEvent = Object.assign({}, initialData);

    bookingEvent.availabilityTimeSlots =
      initialData?.availabilityTimeSlots?.length > 0
        ? initialData.availabilityTimeSlots.map(ts => {
            return {
              id: ts.id,
              dayOfWeek: ts.dayOfWeek,
              startTime: Timespan.parse(ts.startTime).format(),
              endTime: Timespan.parse(ts.endTime).format(),
            } as BookingAvailabilityTimeSlot;
          })
        : [];

    bookingEvent.availabilityDays =
      initialData.availabilityDays?.length > 0
        ? initialData.availabilityDays.map(ad => {
            const startTime = ad.startTime ? Timespan.parse(ad.startTime).format() : null;
            const endTime = ad.endTime ? Timespan.parse(ad.endTime).format() : null;

            return {
              id: ad.id,
              date: ad.date,
              registrationsCount: ad.registrationsCount,
              startTime: startTime,
              endTime: endTime,
              isUnavailable: ad.isUnavailable,
            } as BookingAvailabilityDay;
          })
        : [];

    return bookingEvent;
  }

  return {} as BookingEvent;
}
